/* eslint-disable complexity -- ported 1:1 from the design logic. */
// LP Holiday Lighting – vanilla port of the Claude Design page logic.
//
// The markup (lib/acf/sections/lp-holiday/markup-*.php) is a static snapshot of the design.
// Elements whose attributes/text depend on state carry `data-lpb` = { a: { attr: template },
// t: [ expr per .sc-interp child ], e: { event: expr }, v/l/i: loop var, list and index }.
// `renderVals()` below mirrors the design's logic; `render()` re-applies the bindings.

const reducedMotion = () => window.matchMedia && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

class LpHoliday {
	constructor( root ) {
		this.root = root;
		this.city = root.dataset.city;
		this.region = root.dataset.region;
		this.imgBase = root.dataset.img;
		this.state = { mobile: false, open: null, narrow: false, review: 0 };
		this.vals = null;

		this.binds = [ ...root.querySelectorAll( '[data-lpb]' ) ].map( ( el ) => {
			const b = JSON.parse( el.dataset.lpb );
			b.el = el;
			b.cache = {};
			return b;
		} );

		this.vals = this.renderVals();
		this.binds.forEach( ( b ) => {
			Object.keys( b.e || {} ).forEach( ( type ) => {
				b.el.addEventListener( type, ( e ) => {
					const fn = this.get( b.e[ type ], this.scope( b ) );

					if ( typeof fn === 'function' ) {
						fn( e );
					}
				}, type.startsWith( 'touch' ) ? { passive: true } : false );
			} );
		} );

		this.render();
		this.componentDidMount();
	}

	img( name ) {
		return this.imgBase + name;
	}

	setState( partial ) {
		const next = typeof partial === 'function' ? partial( this.state ) : partial;
		this.state = Object.assign( {}, this.state, next );

		if ( !this.pending ) {
			this.pending = true;
			Promise.resolve().then( () => {
				this.pending = false;
				this.render();
			} );
		}
	}

	scope( b ) {
		if ( !b.v ) {
			return this.vals;
		}

		const list = this.vals[ b.l ] || [];

		return Object.assign( {}, this.vals, { [ b.v ]: list[ b.i ] } );
	}

	get( expr, scope ) {
		if ( expr === 'false' ) {
			return false;
		}

		if ( expr === 'true' ) {
			return true;
		}

		return expr.split( '.' ).reduce( ( o, k ) => ( o === null || o === undefined ? o : o[ k ] ), scope );
	}

	interp( tpl, scope ) {
		return tpl.replace( /\{\{\s*([\w.]+)\s*\}\}/g, ( m, expr ) => {
			const v = this.get( expr, scope );

			return v === null || v === undefined ? '' : String( v );
		} );
	}

	render() {
		this.vals = this.renderVals();

		this.binds.forEach( ( b ) => {
			const scope = this.scope( b );

			Object.keys( b.a || {} ).forEach( ( name ) => {
				const value = this.interp( b.a[ name ], scope );

				if ( b.cache[ name ] === value ) {
					return;
				}

				b.cache[ name ] = value;

				// Leave WP Rocket's lazy-load placeholder alone: it swaps in data-lazy-src itself.
				if ( name === 'src' && b.el.dataset.lazySrc === value ) {
					return;
				}

				if ( name === 'style' ) {
					b.el.style.cssText = value;
				} else {
					b.el.setAttribute( name, value );
				}
			} );

			if ( b.t ) {
				const spans = b.el.querySelectorAll( ':scope > .sc-interp' );

				b.t.forEach( ( expr, k ) => {
					const value = String( this.get( expr, scope ) ?? '' );

					if ( spans[ k ] && spans[ k ].textContent !== value ) {
						spans[ k ].textContent = value;
					}
				} );
			}
		} );
	}

	componentDidMount() {
		this._hwManual = 0;
		this._hwScroll = () => {
			const hw = this.root.querySelector( '#lp-process' );

			if ( !hw || hw.offsetWidth <= 760 || Date.now() < this._hwManual ) {
				return;
			}

			const r = hw.getBoundingClientRect(); const vh = window.innerHeight || 800;

			if ( r.bottom < 0 || r.top > vh ) {
				return;
			}

			const p = ( vh * 0.62 - r.top ) / Math.max( r.height, 1 );
			const i = p < 0.4 ? 0 : p < 0.72 ? 1 : 2;

			if ( i !== ( this.state.hwA ?? 0 ) ) {
				this.setState( { hwA: i } );
			}
		};
		document.addEventListener( 'scroll', this._hwScroll, true );

		// Reviews carousel (mobile scroll-snap).
		const t = this.root.querySelector( '.lp-rv-track' ); const c = this.root.querySelector( '.lp-rv-ctrl' );

		if ( t && c ) {
			const dots = [ ...c.querySelectorAll( '.lp-rv-dot' ) ]; const n = dots.length; const w = () => t.clientWidth; const cur = () => Math.round( t.scrollLeft / w() );
			const paint = () => {
				const a = cur();

				dots.forEach( ( d, k ) => {
					const s = d.firstChild;
					s.style.width = k === a ? '22px' : '8px';
					s.style.background = k === a ? '#389447' : '#D6D0BF';
					d.setAttribute( 'aria-current', k === a ? 'true' : 'false' );
				} );
			};
			const go = ( i ) => t.scrollTo( { left: ( ( i + n ) % n ) * w(), behavior: 'smooth' } );

			dots.forEach( ( d, k ) => d.addEventListener( 'click', () => go( k ) ) );
			c.querySelectorAll( '.lp-rv-arrow' ).forEach( ( b ) => b.addEventListener( 'click', () => go( cur() + Number( b.dataset.dir ) ) ) );
			t.addEventListener( 'scroll', paint, { passive: true } );
			paint();
		}

		this._wyT = setInterval( () => {
			if ( !this.state.wyPaused && !reducedMotion() ) {
				this.goWy( ( ( this.state.wyA ?? 0 ) + 1 ) % 4 );
			}
		}, 6000 );

		this._onResize = () => this.setState( { narrow: window.innerWidth < 760 } );
		this._onResize();
		window.addEventListener( 'resize', this._onResize );
	}

	goWy( i ) {
		this.setState( { wyA: i, wyP: 0 } );
		requestAnimationFrame( () => requestAnimationFrame( () => this.setState( { wyP: 1 } ) ) );
	}

	renderVals() {
		const HWA = this.state.hwA ?? 0;
		const HW = [ [ this.img( 'process-planning.webp' ), 'Brick home with custom holiday lighting on the roofline and trees', 'center 40%', 'Planning', 'Custom setups designed for your home.' ], [ this.img( 'process-install.webp' ), 'Master Lawn crew on a lift adding garland to a large decorated holiday tree at a shopping center', 'center 22%', 'Installation', 'Professional-grade lights. No tangled cords or wobbly ladders.' ], [ this.img( 'process-takedown.webp' ), 'Master Lawn crew member working on a large decorated holiday tree at a shopping center', 'center 40%', 'Take Down', 'We handle the end-of-season takedown.' ] ];
		const hwGo = ( i, manual ) => {
			if ( manual ) {
				this._hwManual = Date.now() + 1800;
			}
			this.setState( { hwA: ( i + 3 ) % 3 } );
		};

		const mobile = this.state.mobile;
		const faqData = [
			[ 'What does your holiday lighting service include?', 'Master Lawn handles your holiday lighting from start to finish, using professional-grade lights and custom setups designed for your home. We handle the planning and installation.' ],
			[ 'Is the design custom to my home?', 'Yes. We install custom holiday lighting to bring your seasonal setups to the next level, with setups designed for your home.' ],
			[ 'What kind of lights do you use?', 'We use professional-grade lights.' ],
			[ 'Do I need to get on a ladder?', 'No. Skip the tangled cords and wobbly ladders this year. All you have to do is enjoy the glow.' ],
			[ 'How do I get started?', 'Request a free quote by calling us or sending the form, and we\'ll take it from there.' ],
		];

		const city = this.city; const region = this.region;

		return {
			hwSteps: HW.map( ( s, i ) => ( { img: s[ 0 ], alt: s[ 1 ], pos: s[ 2 ], title: s[ 3 ], copy: s[ 4 ], num: '0' + ( i + 1 ),
				sel: i === HWA ? 'true' : 'false', hidden: i === HWA ? 'false' : 'true', op: i === HWA ? 1 : 0, zoom: i === HWA ? 1.07 : 1,
				bg: i === HWA ? '#FFFFFF' : 'transparent', sh: i === HWA ? '0 10px 28px rgba(31,36,24,.08)' : 'none', x: i === HWA ? '4px' : '0px',
				dotBg: i <= HWA ? '#F5A623' : '#FFFFFF', dotBorder: i <= HWA ? '#F5A623' : '#C9C2AF',
				numSize: i === HWA ? '22px' : '18px', numColor: i === HWA ? '#B07A14' : '#A3A996', titleSize: i === HWA ? '24px' : '20px',
				rows: i === HWA ? '1fr' : '0fr',
				pick: () => {
					if ( ( this.state.hwA ?? 0 ) !== i ) {
						hwGo( i, true );
					} else {
						this._hwManual = Date.now() + 1800;
					}
				} } ) ),
			hwFill: ( HWA * 50 ) + '%', hwBar: ( ( HWA + 1 ) / 3 * 100 ) + '%', hwNum: '0' + ( HWA + 1 ), hwTitle: HW[ HWA ][ 3 ], hwCopy: HW[ HWA ][ 4 ],
			hwPrev: () => hwGo( HWA - 1, true ), hwNext: () => hwGo( HWA + 1, true ),
			hwTouchStart: ( e ) => {
				this._hwX = e.touches && e.touches[ 0 ] ? e.touches[ 0 ].clientX : null;
			},
			hwTouchEnd: ( e ) => {
				if ( this._hwX === null || this._hwX === undefined ) {
					return;
				}
				const x = e.changedTouches && e.changedTouches[ 0 ] ? e.changedTouches[ 0 ].clientX : this._hwX;
				const d = x - this._hwX;
				this._hwX = null;
				if ( Math.abs( d ) > 40 ) {
					hwGo( HWA + ( d < 0 ? 1 : -1 ), true );
				}
			},
			...( () => {
				const S = [
					{ name: 'Lawn Care & Weed Control', img: this.img( 'services-home.webp' ), pos: 'center center', alt: 'Thick weed-free lawn in front of a ' + city + ' home maintained by Master Lawn', lens: [ 4, 76, 44, 21 ], short: 'Weed control and ongoing treatments for a cleaner, thicker lawn.', copy: 'Professional weed control and ongoing lawn treatments keep your lawn healthy, green, and well maintained. The result is a cleaner, thicker, more consistent lawn.' },
					{ name: 'Fertilization', img: this.img( 'services-home.webp' ), pos: 'center center', alt: city + ' home and lawn cared for by Master Lawn', lens: [ 62, 70, 36, 16 ], short: 'Season-specific feeding for stronger roots and richer color.', copy: 'Season-specific feeding gives your lawn the nutrients it needs for healthy growth, stronger roots, and richer color. Your turf fills in fuller and holds its color longer.' },
					{ name: 'Aeration & Overseeding', img: this.img( 'services-home.webp' ), pos: 'center center', alt: city + ' home and lawn cared for by Master Lawn', lens: [ 29, 62, 12, 15 ], short: 'Opens compacted soil so water, air, and nutrients reach the roots.', copy: 'Aeration breaks up compacted soil so water, air, and nutrients reach the roots. Paired with overseeding, it helps thin areas fill in with thicker grass.' },
					{ name: 'Pest Control', img: this.img( 'services-home.webp' ), pos: 'center center', alt: city + ' home and lawn cared for by Master Lawn', lens: [ 42, 63, 36, 9 ], short: 'Targeted treatments for mosquitoes, fire ants, fleas, and grubs.', copy: 'Targeted treatments help protect your home and lawn from mosquitoes, fire ants, fleas, and grubs. A barrier around the home perimeter keeps pests out of your outdoor space.' },
					{ name: 'Tree & Shrub Care', img: this.img( 'services-home.webp' ), pos: 'center center', alt: city + ' home and lawn cared for by Master Lawn', lens: [ 62, 56, 21, 16 ], short: 'Care for healthier trees, shrubs, and landscape beds.', copy: 'Professional treatments and ongoing care support healthier trees, shrubs, and landscape beds. Problems are caught early, so your landscaping stays full and vibrant.' },
					{ name: 'Holiday Lighting', img: this.img( 'services-home.webp' ), pos: 'center center', alt: city + ' home with warm holiday lights along the roofline, tree, and shrubs', lens: [ 3, 4, 94, 92 ], short: 'Holiday lights designed, installed, and taken down for you.', copy: 'Custom holiday lighting designed for your home, installed with professional-grade lights, so no ladder is required.' },
				];
				const A = this.state.lnA ?? 5;
				const a = S[ A ];
				const [ l, t, w, h ] = a.lens;
				const pick = ( i ) => () => {
					const cur = this.state.lnA ?? 5;
					if ( cur !== i ) {
						this.setState( { lnA: i, lnP: cur } );
					}
				};
				const num = ( i ) => '0' + ( i + 1 );
				const right = l + w <= 66;

				return {
					lnItems: S.map( ( s, i ) => ( {
						num: num( i ), name: s.name, short: s.short, bg: i === A ? '#FFFFFF' : 'transparent', pick: pick( i ), sel: i === A ? 'true' : 'false',
						bar: i === A ? '#389447' : '#DCD5C3', numSize: i === A ? '30px' : '22px',
						img: s.img, pos: s.pos, alt: s.alt, copy: s.copy, seasonal: i === 5,
						rows: i === A ? '1fr' : '0fr', shortOp: i === A ? 1 : 0,
						flex: i === A ? '4.4 1 0' : '1 1 0', zoom: i === A ? 1.03 : 1.14, shade: i === A ? '.08' : '.3',
						gNum: i === A ? '34px' : '22px', gOp: i === A ? 1 : 0, gY: i === A ? '0px' : '16px', gD: i === A ? '320ms' : '0ms',
						gVert: i === A ? 0 : 1, gLine: i === A ? '48px' : '0px', gLineC: i === 5 ? '#F5A623' : '#C9A24A',
						numColor: i === A ? '#B07A14' : '#A3A996', nameColor: i === A ? '#1F3A22' : '#6B7064', nameX: i === A ? '3px' : '0px',
					} ) ),
					lnImgs: [ {
						img: S[ 0 ].img, pos: S[ 0 ].pos, alt: S[ A ].alt, hid: A === 5 ? 'true' : 'false', z: 1, clip: 'none',
						scale: 1, trans: 'none',
					}, {
						img: this.img( 'night-house.webp' ), pos: 'center 40%', alt: 'Home with professionally installed holiday lighting along every roofline', hid: A === 5 ? 'false' : 'true', z: 2,
						clip: A === 5 ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)', scale: A === 5 ? 1.04 : 1,
						trans: 'clip-path 900ms cubic-bezier(.77,0,.18,1), transform 4s ease',
					} ],
					lnFx0: A === 0 ? 1 : 0, lnFx1: A === 1 ? 1 : 0, lnFx2: A === 2 ? 1 : 0, lnFx3: A === 3 ? 1 : 0, lnFx4: A === 4 ? 1 : 0, lnFx5: 0,
					lnL: l + '%', lnT: t + '%', lnW: w + '%', lnH: h + '%',
					lnPL: right ? 'calc(' + ( l + w ) + '% + 20px)' : l >= 34 ? 'calc(' + l + '% - min(300px,32%) - 20px)' : l + '%',
					lnPT: right || l >= 34 ? 'min(' + t + '%, calc(100% - 210px))' : 'calc(' + ( t + h ) + '% + 16px)',
					lnBigY: '-' + A + 'em',
					lnNum: num( A ), lnName: a.name, lnCopy: a.copy, lnHoliday: A === 5,
				};
			} )(),
			wyPause: () => this.setState( { wyPaused: true } ),
			wyResume: () => {
				this.setState( { wyPaused: false } );
				this.goWy( this.state.wyA ?? 0 );
			},
			wyPrev: () => this.goWy( ( ( this.state.wyA ?? 0 ) + 3 ) % 4 ),
			wyNext: () => this.goWy( ( ( this.state.wyA ?? 0 ) + 1 ) % 4 ),
			wyItems: ( () => {
				const W = [
					{ title: 'A plan built for your lawn, not a template', copy: 'Every program starts with a free assessment of your property, not a generic package. Your treatment plan is built around what your lawn actually needs.' },
					{ title: 'Clear communication, every step', copy: 'You\'ll get a text before your technician arrives, and they\'ll ask before stepping into your back lawn. No surprises, no guessing who\'s on your property.' },
					{ title: 'Over 30 years of lawn care, local to ' + city, copy: 'Master Lawn has been treating lawns for over 30 years, and our team has worked on lawns across ' + city + ' for years, so we understand what makes ' + region + ' lawns thrive.' },
					{ title: 'Backed by Michael Hatcher & Associates', copy: 'Master Lawn is part of Michael Hatcher & Associates, a landscape company with decades of regional experience, so you get the backing of an established company with the attentiveness of a local lawn care team.' },
				];
				const A = this.state.wyA ?? 0; const P = this.state.wyPaused; const G = this.state.wyP ?? 1;
				const IM = [ [ this.img( 'why-plan.webp' ), 'Master Lawn technician reviewing a lawn care plan on a clipboard' ], [ this.img( 'why-communication.webp' ), 'Master Lawn technician shaking hands with a homeowner' ], [ this.img( 'why-local.webp' ), 'Master Lawn technician on a ride-on spreader with a Master Lawn truck behind' ], [ this.img( 'why-hatcher.webp' ), 'Master Lawn truck parked outside the Michael Hatcher & Associates building' ] ];

				return W.map( ( w, i ) => ( {
					...w, pos: i === 2 ? '50% 28%' : i === 1 ? '50% 28%' : '50% 50%', short: [ 'Custom plan', 'Clear updates', '30+ years local', 'Hatcher backed' ][ i ], num: '0' + ( i + 1 ), i0: i === 0, i1: i === 1, i2: i === 2, i3: i === 3, img: IM[ i ][ 0 ], alt: IM[ i ][ 1 ],
					sel: i === A ? 'true' : 'false', op: i === A ? 1 : 0.6, tNum: i === A ? '#F5D48A' : 'rgba(255,255,255,.55)', line: i === A ? '64px' : '0px', hidden: i === A ? 'false' : 'true',
					fOp: i === A ? 1 : 0, fVis: i === A ? 'visible' : 'hidden', fDelay: i === A ? '0s' : '900ms',
					clip: i === A ? 'inset(0 0 0 0)' : 'inset(0 100% 0 0)', zoom: i === A ? 1.08 : 1,
					bigOp: i === A ? 0.1 : 0, bigY: i === A ? '0px' : '30px', y1: i === A ? '0px' : '14px', iconS: i === A ? 1 : 0.8,
					d1: i === A ? '250ms' : '0ms', d2: i === A ? '340ms' : '0ms', d3: i === A ? '430ms' : '0ms',
					barOp: i === A ? 1 : 0, prog: i === A ? ( P ? '100%' : ( G ? '100%' : '0%' ) ) : '0%',
					progT: i === A && G && !P ? 'width 6000ms linear' : 'none',
					pick: () => {
						if ( ( this.state.wyA ?? 0 ) !== i ) {
							this.goWy( i );
						}
					},
				} ) );
			} )(),
			callBarDisplay: ( mobile || this.state.narrow ) ? 'flex' : 'none',
			footerPad: '0px',
			headerCtaDisplay: ( mobile || this.state.narrow ) ? 'none' : 'flex',
			headerMobileDisplay: ( mobile || this.state.narrow ) ? 'flex' : 'none',
			faqBar: ( ( ( this.state.faqA ?? 0 ) + 1 ) / faqData.length * 100 ) + '%',
			faqs: faqData.map( ( [ q, a ], i ) => {
				const on = ( this.state.faqA ?? 0 ) === i;

				return {
					q, a, num: '0' + ( i + 1 ), sel: on ? 'true' : 'false', hid: on ? 'false' : 'true',
					bg: on ? '#F7F2E9' : 'transparent', nc: on ? '#B07A14' : '#A3A996', tc: on ? '#1F2A1C' : '#4A4F44',
					ar: on ? 1 : 0, ax: on ? '0px' : '-6px', vis: on ? 'visible' : 'hidden', vd: on ? '0s' : '450ms',
					op2: on ? 1 : 0, y: on ? '0px' : '12px',
					pick: () => {
						if ( ( this.state.faqA ?? 0 ) !== i ) {
							this.setState( { faqA: i } );
						}
					},
					tap: () => this.setState( ( s ) => ( { faqA: i, faqM: s.faqM === i ? -1 : i } ) ),
					mOpen: ( this.state.faqM ?? 0 ) === i ? 'true' : 'false', mHid: ( this.state.faqM ?? 0 ) === i ? 'false' : 'true',
					mRows: ( this.state.faqM ?? 0 ) === i ? '1fr' : '0fr',
				};
			} ),
		};
	}
}

const initLpHoliday = () => {
	document.querySelectorAll( '.section-lp-holiday' ).forEach( ( root ) => {
		if ( !root.lpHoliday ) {
			root.lpHoliday = new LpHoliday( root );
		}
	} );
};

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initLpHoliday );
} else {
	initLpHoliday();
}
